/**
 * The records a task is about.
 * Spec: docs/superpowers/specs/2026-10-26-task-links-design.md
 *
 * A task links to any number of companies and people, up to MAX_TASK_LINKS.
 * The first company and the first person live in `tasks.company_id` and
 * `tasks.contact_id`, which every existing writer (the reminder bell, the
 * Outlook add-in, Next step, project-task copies) already sets. Everything
 * after them lives in `task_links`. Same shape as `activities.contact_id`
 * plus `contact_ids`: single-record readers keep working untouched.
 *
 * Pure, so the server, the client and the morning email agree on the rules.
 */

export type TaskLinkKind = "company" | "contact";

export interface TaskLinkRef {
  kind: TaskLinkKind;
  id: string;
}

/** A link as readers display it. `companyName` is set for people, so a chip can say where they work. */
export interface TaskLink extends TaskLinkRef {
  name: string;
  category: string | null;
  companyName: string | null;
}

/** Counts every link, the two column links included. */
export const MAX_TASK_LINKS = 8;

export interface TaskLinkPlan {
  companyId: string | null;
  contactId: string | null;
  extras: Array<TaskLinkRef & { position: number }>;
}

const KINDS: ReadonlySet<string> = new Set<TaskLinkKind>(["company", "contact"]);

/**
 * Validate a request body's `links`. Dedupes on kind and id, keeping the first
 * occurrence, and applies the cap after dedupe so a repeated pick is not
 * counted twice.
 */
export function parseTaskLinkRefs(raw: unknown): { refs: TaskLinkRef[] } | { error: string } {
  if (!Array.isArray(raw)) return { error: "links must be a list" };
  const refs: TaskLinkRef[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    const kind = (entry as { kind?: unknown } | null)?.kind;
    const id = (entry as { id?: unknown } | null)?.id;
    if (typeof kind !== "string" || !KINDS.has(kind)) return { error: "Each link needs a kind of company or contact." };
    if (typeof id !== "string" || !id.trim()) return { error: "Each link needs an id." };
    const ref = { kind: kind as TaskLinkKind, id: id.trim() };
    const key = `${ref.kind}:${ref.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    refs.push(ref);
  }
  if (refs.length > MAX_TASK_LINKS) return { error: `A task can link to at most ${MAX_TASK_LINKS} records.` };
  return { refs };
}

/** The first company and first person go to the columns; the rest keep their order as extras. */
export function planTaskLinks(refs: TaskLinkRef[]): TaskLinkPlan {
  let companyId: string | null = null;
  let contactId: string | null = null;
  const extras: TaskLinkPlan["extras"] = [];
  for (const r of refs) {
    if (r.kind === "company" && companyId === null) companyId = r.id;
    else if (r.kind === "contact" && contactId === null) contactId = r.id;
    else extras.push({ ...r, position: extras.length });
  }
  return { companyId, contactId, extras };
}

/** "Acme Home Health", or "Jane Smith · Barnes Plumbing" for a person. */
export function taskLinkLabel(l: Pick<TaskLink, "kind" | "name" | "companyName">): string {
  return l.kind === "contact" && l.companyName ? `${l.name} · ${l.companyName}` : l.name;
}

/** A task's links minus the record the viewer is already on. */
export function linksExcept(links: TaskLink[], ref: TaskLinkRef | null): TaskLink[] {
  return ref ? links.filter((l) => !(l.kind === ref.kind && l.id === ref.id)) : links;
}
